import { ArrowLeftToLine } from 'lucide-react';
import * as React from 'react';

import navMenu from '@/app/navMenu';
import logo from '@/assets/logo_small_black.png';
import textLogo from '@/assets/logo_text_black.png';
import { NavDev } from '@/components/Sidebar/nav-dev';
import { NavMain } from '@/components/Sidebar/nav-main';
import { NavPinned } from '@/components/Sidebar/nav-pinned';
import { NavProjects } from '@/components/Sidebar/nav-projects';
import { NavUser } from '@/components/Sidebar/nav-user';
import { VersionBadge } from '@/components/VersionBadge';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/Sidebar/sidebar';
import { useSidebar } from '@/components/ui/Sidebar/SidebarContextProps';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsMac } from '@/hooks/useIsMac';
import { Kbd, KbdGroup } from '@/components/ui/kbd';

function InnerAppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { toggleSidebar, open, isMobile } = useSidebar();
  const isMac = useIsMac();

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent
                data-[state=open]:text-sidebar-accent-foreground flex w-full items-center
                opacity-100!"
            >
              <div
                className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8
                  items-center justify-center rounded-lg dark:bg-transparent"
              >
                <img
                  src={logo}
                  alt="Certhon Thumbnail Logo"
                  className="size-6 rounded-md"
                />
              </div>
              <img
                src={textLogo}
                alt="Certhon Text Logo"
                className="-mt-1 h-6 dark:invert"
              />
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavPinned />
        <NavMain items={navMenu} />
        <NavProjects />
        <NavDev />
      </SidebarContent>
      <SidebarFooter>
        <div className="group-data-[collapsible=icon]:hidden">
          <VersionBadge />
        </div>

        {!isMobile && (
          <SidebarMenu>
            <SidebarMenuItem>
              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarMenuButton
                    onClick={toggleSidebar}
                    className="text-muted-foreground w-full justify-start"
                  >
                    <ArrowLeftToLine
                      className={`size-4 transition-transform ${!open ? 'rotate-180' : ''}`}
                    />
                    <span className="group-data-[collapsible=icon]:hidden">
                      Collapse sidebar
                    </span>
                  </SidebarMenuButton>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>
                    Toggle sidebar (
                    <KbdGroup>
                      <Kbd>{isMac ? '⌘' : 'Ctrl'}</Kbd>+<Kbd>B</Kbd>
                    </KbdGroup>
                    )
                  </p>
                </TooltipContent>
              </Tooltip>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

export const AppSidebar = React.memo(InnerAppSidebar);
